import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  PHASE_ORDER,
  PHASE_COLOR,
  ConceptDetail,
  PendingCandidate,
  btnSmall,
  emptyStyle,
} from "./WorldsPage";

/**
 * /workspace/vessels — Phase 2: the Vessel envelope.
 *
 * A Vessel is SONMI's persistent representation of a business domain
 * (see /app/CANON/VESSEL.md). This page renders the four Vessel parts
 * — World, Evidence, Activity, People — for the selected Vessel.
 *
 * The World section renders the existing World inspector inside its
 * own titled sub-section. Nothing was renamed. The World is visibly
 * contained by a Vessel; it did not become one.
 */

export default function VesselsPage() {
  const [vessels, setVessels] = useState([]);
  const [vesselId, setVesselId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [selectedConcept, setSelectedConcept] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addEvidenceOpen, setAddEvidenceOpen] = useState(false);

  const refreshList = useCallback(async () => {
    const r = await api.get("/vessels");
    setVessels(r.data);
    if (r.data.length && !vesselId) setVesselId(r.data[0].id);
    setLoading(false);
  }, [vesselId]);

  useEffect(() => {
    refreshList();
  }, [refreshList]);

  const loadDetail = useCallback(async (id) => {
    const r = await api.get(`/vessels/${id}`);
    setDetail(r.data);
  }, []);

  useEffect(() => {
    if (!vesselId) {
      setDetail(null);
      return;
    }
    loadDetail(vesselId);
  }, [vesselId, loadDetail]);

  const approveCandidate = async (cid, reason) => {
    await api.post(
      `/worlds/${detail.world.id}/pending/${cid}/approve`,
      reason ? { reason } : {}
    );
    loadDetail(vesselId);
  };
  const rejectCandidate = async (cid, reason) => {
    await api.post(
      `/worlds/${detail.world.id}/pending/${cid}/reject`,
      reason ? { reason } : {}
    );
    loadDetail(vesselId);
  };

  return (
    <div
      data-testid="vessels-page"
      style={{
        height: "100vh",
        background: "#050508",
        color: "rgba(255,255,255,0.88)",
        fontFamily: "'JetBrains Mono', 'SF Mono', Menlo, monospace",
        fontSize: 12,
        display: "grid",
        gridTemplateColumns: "260px 1fr",
        overflow: "hidden",
      }}
    >
      {/* Left rail — Vessel list */}
      <div
        style={{
          borderRight: "1px solid rgba(255,255,255,0.1)",
          padding: 16,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <div
          style={{
            fontSize: 10,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            opacity: 0.55,
          }}
        >
          SONMI · Vessels
        </div>
        {loading && <div style={emptyStyle}>loading…</div>}
        {!loading && vessels.length === 0 && (
          <div style={emptyStyle}>
            No Vessels yet. A Vessel arrives with its first World.
          </div>
        )}
        {vessels.map((v) => (
          <div
            key={v.id}
            data-testid={`vessel-${v.id}`}
            onClick={() => setVesselId(v.id)}
            style={{
              padding: "8px 10px",
              borderRadius: 4,
              cursor: "pointer",
              background: v.id === vesselId ? "rgba(255,255,255,0.06)" : "transparent",
              border: `1px solid ${
                v.id === vesselId ? "rgba(255,255,255,0.16)" : "transparent"
              }`,
            }}
          >
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.92)" }}>
              {v.name}
            </div>
            <div style={{ fontSize: 10, opacity: 0.5, marginTop: 4 }}>
              contains World: {v.world_name}
            </div>
          </div>
        ))}
        <div style={{ marginTop: "auto", fontSize: 10, opacity: 0.4 }}>
          <a
            href="/workspace/debug"
            style={{ color: "inherit", textDecoration: "none" }}
          >
            ← debug
          </a>
          {"  ·  "}
          <a
            href="/workspace"
            style={{ color: "inherit", textDecoration: "none" }}
          >
            workspace
          </a>
        </div>
      </div>

      {/* Right — Vessel view (four sections) */}
      <div style={{ padding: 20, overflowY: "auto" }}>
        {!detail && <div style={emptyStyle}>select a vessel</div>}
        {detail && (
          <>
            <VesselHeader
              vessel={detail.vessel}
              onAddEvidence={() => setAddEvidenceOpen(true)}
            />

            <VesselSection title="World" hint="What SONMI understands.">
              <WorldContent
                world={detail.world}
                onApprove={approveCandidate}
                onReject={rejectCandidate}
                onSelectConcept={(id) => setSelectedConcept(id)}
              />
            </VesselSection>

            <VesselSection
              title="Evidence"
              hint="What this Vessel has been shown."
            >
              <EvidenceContent evidence={detail.evidence} />
            </VesselSection>

            <VesselSection
              title="Activity"
              hint="How the Vessel's understanding changed."
            >
              <ActivityContent activity={detail.activity} />
            </VesselSection>

            <VesselSection
              title="People"
              hint="Who participates in this domain, where evidence supports it."
            >
              <PeopleContent people={detail.people} />
            </VesselSection>
          </>
        )}
      </div>

      {selectedConcept && detail && (
        <ConceptDetail
          worldId={detail.world.id}
          conceptId={selectedConcept}
          onClose={() => setSelectedConcept(null)}
        />
      )}

      {addEvidenceOpen && detail && (
        <AddEvidenceModal
          vessel={detail.vessel}
          worldId={detail.world.id}
          onClose={() => setAddEvidenceOpen(false)}
          onAdded={() => {
            setAddEvidenceOpen(false);
            loadDetail(vesselId);
          }}
        />
      )}
    </div>
  );
}

/* ---------- Vessel header ---------- */
function VesselHeader({ vessel, onAddEvidence }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 20,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 10,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              opacity: 0.55,
              marginBottom: 6,
            }}
          >
            Vessel
          </div>
          <div
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 26,
              fontWeight: 300,
            }}
          >
            {vessel.name}
          </div>
          {vessel.description && (
            <div
              style={{
                fontFamily: "'Inter', sans-serif",
                fontStyle: "italic",
                fontSize: 13,
                opacity: 0.7,
                marginTop: 6,
                maxWidth: 640,
              }}
            >
              {vessel.description}
            </div>
          )}
          <div
            style={{
              fontSize: 10,
              opacity: 0.4,
              marginTop: 8,
              letterSpacing: "0.06em",
            }}
          >
            opened {vessel.created_at ? new Date(vessel.created_at).toLocaleString() : "—"}
          </div>
        </div>
        <button
          data-testid="vessel-add-evidence"
          onClick={onAddEvidence}
          style={{
            ...btnSmall,
            padding: "10px 16px",
            fontSize: 12,
            borderColor: "rgba(180,240,200,0.35)",
            color: "rgba(200,240,215,0.95)",
          }}
        >
          + Add evidence
        </button>
      </div>
    </div>
  );
}

/* ---------- Section shell ---------- */
function VesselSection({ title, hint, children }) {
  return (
    <section
      data-testid={`vessel-section-${title.toLowerCase()}`}
      style={{
        marginBottom: 32,
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 6,
        padding: "16px 18px",
        background: "rgba(255,255,255,0.015)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 12,
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          paddingBottom: 8,
        }}
      >
        <div
          style={{
            fontSize: 10,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            opacity: 0.7,
          }}
        >
          {title}
        </div>
        {hint && (
          <div
            style={{
              fontFamily: "'Inter', sans-serif",
              fontStyle: "italic",
              fontSize: 11,
              opacity: 0.45,
            }}
          >
            {hint}
          </div>
        )}
      </div>
      {children}
    </section>
  );
}

/* ---------- World section content ---------- */
function WorldContent({ world, onApprove, onReject, onSelectConcept }) {
  const conceptsByPhase = {};
  for (const p of PHASE_ORDER) conceptsByPhase[p] = [];
  for (const c of world.concepts || []) {
    (conceptsByPhase[c.phase] || (conceptsByPhase[c.phase] = [])).push(c);
  }
  return (
    <>
      <div
        style={{
          fontSize: 11,
          opacity: 0.6,
          marginBottom: 12,
          fontFamily: "'Inter', sans-serif",
          fontStyle: "italic",
        }}
      >
        The World inside this Vessel is named <b>{world.name}</b>. It contains{" "}
        {world.concepts.length} concept{world.concepts.length === 1 ? "" : "s"}.
        Nothing here has been renamed — the World remains a World.
      </div>

      {world.pending && world.pending.length > 0 && (
        <div
          data-testid="vessel-pending-review"
          style={{
            marginBottom: 20,
            padding: 12,
            border: "1px solid rgba(255,220,140,0.28)",
            borderRadius: 6,
            background: "rgba(255,220,140,0.03)",
          }}
        >
          <div
            style={{
              fontSize: 10,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "rgba(255,220,140,0.85)",
              marginBottom: 10,
              display: "flex",
              justifyContent: "space-between",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <span>
              Under review · {world.pending.length} proposal
              {world.pending.length === 1 ? "" : "s"}
            </span>
            <span
              style={{
                opacity: 0.55,
                letterSpacing: "0.04em",
                textTransform: "none",
              }}
            >
              nothing enters shared understanding until it has been reviewed
            </span>
          </div>
          <ExtractionDisciplinePanel />
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {world.pending.map((c) => (
              <PendingCandidate
                key={c.id}
                c={c}
                onApprove={(r) => onApprove(c.id, r)}
                onReject={(r) => onReject(c.id, r)}
              />
            ))}
          </div>
        </div>
      )}

      {PHASE_ORDER.filter((p) => (conceptsByPhase[p] || []).length > 0).map(
        (phase) => (
          <div key={phase} style={{ marginBottom: 18 }}>
            <div
              style={{
                fontSize: 10,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: PHASE_COLOR[phase],
                marginBottom: 6,
                display: "flex",
                gap: 8,
                alignItems: "baseline",
              }}
            >
              <span>{phase}</span>
              <span style={{ opacity: 0.5, fontSize: 9 }}>
                ({(conceptsByPhase[phase] || []).length})
              </span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {(conceptsByPhase[phase] || []).map((c) => (
                <div
                  key={c.id}
                  data-testid={`concept-${c.id}`}
                  onClick={() => onSelectConcept(c.id)}
                  style={{
                    padding: "6px 10px",
                    border: `1px solid ${PHASE_COLOR[phase]}`,
                    borderRadius: 12,
                    background:
                      phase === "encountered"
                        ? "transparent"
                        : `${PHASE_COLOR[phase].replace(/[\d.]+\)$/, "0.08)")}`,
                    fontSize: 11,
                    cursor: "pointer",
                    color: "rgba(255,255,255,0.9)",
                  }}
                >
                  {c.name}
                  <span
                    style={{ opacity: 0.5, marginLeft: 6, fontSize: 9 }}
                  >
                    {c.observation_count}/{c.source_artifact_count}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )
      )}

      {(!world.concepts || world.concepts.length === 0) && (
        <div style={emptyStyle}>
          This World has no shared understanding yet. Add evidence and
          review the proposals that arise.
        </div>
      )}
    </>
  );
}

/* ---------- Evidence section content ---------- */
function EvidenceContent({ evidence }) {
  if (!evidence || evidence.length === 0) {
    return (
      <div style={emptyStyle}>
        No evidence has been added to this Vessel yet.
      </div>
    );
  }
  return (
    <div
      style={{ display: "flex", flexDirection: "column", gap: 8 }}
      data-testid="vessel-evidence-list"
    >
      {evidence.map((e) => (
        <div
          key={`${e.session_id}::${e.artifact_id}`}
          data-testid={`evidence-${e.artifact_id}`}
          style={{
            padding: "10px 12px",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 4,
            background: "rgba(255,255,255,0.02)",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 10,
              alignItems: "baseline",
              justifyContent: "space-between",
              flexWrap: "wrap",
            }}
          >
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.94)" }}>
              {e.artifact_title || (
                <span style={{ opacity: 0.5 }}>
                  (artifact {e.artifact_id.slice(0, 8)}
                  {e.artifact_missing ? " · no longer in workspace" : ""})
                </span>
              )}
              {e.artifact_type && (
                <span
                  style={{
                    marginLeft: 10,
                    fontSize: 9,
                    letterSpacing: "0.12em",
                    opacity: 0.55,
                    textTransform: "uppercase",
                  }}
                >
                  {e.artifact_type}
                </span>
              )}
              {e.artifact_source === "upload" && (
                <span
                  style={{
                    marginLeft: 8,
                    fontSize: 9,
                    letterSpacing: "0.12em",
                    opacity: 0.55,
                    textTransform: "uppercase",
                  }}
                >
                  · upload
                </span>
              )}
            </div>
            <div style={{ fontSize: 10, opacity: 0.5 }}>
              first shown{" "}
              {e.first_seen_at
                ? new Date(e.first_seen_at).toLocaleString()
                : "—"}
            </div>
          </div>
          <div
            style={{
              marginTop: 6,
              fontSize: 10,
              opacity: 0.6,
              display: "flex",
              gap: 14,
              flexWrap: "wrap",
              letterSpacing: "0.04em",
            }}
          >
            {e.observation_count > 0 && (
              <span>
                observations: <b>{e.observation_count}</b>
              </span>
            )}
            {e.contradiction_count > 0 && (
              <span style={{ color: "rgba(255,180,180,0.85)" }}>
                contradictions: <b>{e.contradiction_count}</b>
              </span>
            )}
            {e.approved_count > 0 && (
              <span style={{ color: "rgba(180,240,200,0.85)" }}>
                shared: <b>{e.approved_count}</b>
              </span>
            )}
            {e.pending_count > 0 && (
              <span style={{ color: "rgba(255,220,140,0.85)" }}>
                under review: <b>{e.pending_count}</b>
              </span>
            )}
            {e.rejected_count > 0 && (
              <span style={{ color: "rgba(180,180,200,0.7)" }}>
                held: <b>{e.rejected_count}</b>
              </span>
            )}
            {e.session_title && (
              <span style={{ opacity: 0.5 }}>
                from session "{e.session_title}"
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------- Activity section content ---------- */
const ACTIVITY_COLOR = {
  vessel_created: "rgba(255,255,255,0.7)",
  phase_transition: "rgba(180,220,255,0.85)",
  candidate_approved: "rgba(180,240,200,0.85)",
  candidate_rejected: "rgba(200,200,220,0.7)",
  contradiction: "rgba(255,180,180,0.85)",
};
const ACTIVITY_LABEL = {
  vessel_created: "opened",
  phase_transition: "phase",
  candidate_approved: "shared",
  candidate_rejected: "held",
  contradiction: "contradiction",
};

function ActivityContent({ activity }) {
  if (!activity || activity.length === 0) {
    return (
      <div style={emptyStyle}>
        No activity recorded in this Vessel yet.
      </div>
    );
  }
  return (
    <div
      data-testid="vessel-activity-timeline"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        borderLeft: "2px solid rgba(255,255,255,0.1)",
        paddingLeft: 14,
      }}
    >
      {activity.map((a, i) => (
        <div
          key={i}
          style={{
            fontSize: 11,
            lineHeight: 1.5,
            color: "rgba(255,255,255,0.78)",
          }}
        >
          <span
            style={{
              display: "inline-block",
              minWidth: 96,
              fontSize: 9,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: ACTIVITY_COLOR[a.kind] || "rgba(255,255,255,0.55)",
            }}
          >
            {ACTIVITY_LABEL[a.kind] || a.kind}
          </span>
          <span style={{ opacity: 0.5, marginRight: 10 }}>
            {a.at ? new Date(a.at).toLocaleString() : "—"}
          </span>
          {a.kind === "phase_transition" && (
            <span style={{ color: PHASE_COLOR[a.to_phase] }}>
              {a.concept_name}: {a.from_phase || "∅"} → {a.to_phase}
            </span>
          )}
          {a.kind === "contradiction" && a.concept_name && (
            <span style={{ color: "rgba(255,180,180,0.9)" }}>
              {a.concept_name}
            </span>
          )}
          {(a.kind === "candidate_approved" ||
            a.kind === "candidate_rejected") &&
            a.candidate_name && (
              <span style={{ opacity: 0.85 }}>{a.candidate_name}</span>
            )}
          {a.detail && (
            <div
              style={{
                marginLeft: 96,
                fontFamily: "'Inter', sans-serif",
                fontStyle: "italic",
                fontSize: 11,
                opacity: 0.65,
                marginTop: 2,
              }}
            >
              {a.detail}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ---------- People section content ---------- */
const PARTICIPATION_LABEL = {
  owner: "Owner",
  approver: "Approves proposals",
  rejecter: "Rejects proposals",
  evidence_contributor: "Contributes evidence",
};

function PeopleContent({ people }) {
  if (!people || people.length === 0) {
    return (
      <div style={emptyStyle}>
        No participants yet. People appear here only when evidence records
        their participation.
      </div>
    );
  }
  return (
    <div
      data-testid="vessel-people-list"
      style={{ display: "flex", flexDirection: "column", gap: 8 }}
    >
      {people.map((p) => (
        <div
          key={p.user_id}
          data-testid={`person-${p.user_id}`}
          style={{
            padding: "10px 12px",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 4,
            background: "rgba(255,255,255,0.02)",
            display: "flex",
            justifyContent: "space-between",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: 14,
                color: "rgba(255,255,255,0.92)",
              }}
            >
              {p.name}
            </div>
            <div style={{ fontSize: 10, opacity: 0.55, marginTop: 2 }}>
              {p.role || "role unknown"}
              {p.email ? ` · ${p.email}` : ""}
            </div>
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
              alignItems: "center",
            }}
          >
            {(p.participations || []).map((k) => (
              <span
                key={k}
                style={{
                  fontSize: 9,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  padding: "3px 8px",
                  borderRadius: 10,
                  border: "1px solid rgba(255,255,255,0.14)",
                  opacity: 0.85,
                }}
              >
                {PARTICIPATION_LABEL[k] || k}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------- Add evidence modal ---------- */
function AddEvidenceModal({ vessel, worldId, onClose, onAdded }) {
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [artifacts, setArtifacts] = useState([]);
  const [artifactId, setArtifactId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/workspace/sessions").then((r) => setSessions(r.data));
  }, []);

  useEffect(() => {
    if (!sessionId) {
      setArtifacts([]);
      setArtifactId("");
      return;
    }
    api.get(`/workspace/sessions/${sessionId}`).then((r) => {
      setArtifacts(r.data.artifacts || []);
      setArtifactId("");
    });
  }, [sessionId]);

  const submit = async () => {
    if (!sessionId || !artifactId) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.post(`/worlds/${worldId}/observe`, {
        session_id: sessionId,
        artifact_id: artifactId,
      });
      onAdded();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to add evidence");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="add-evidence-modal"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.72)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#0c0c14",
          border: "1px solid rgba(255,255,255,0.14)",
          borderRadius: 6,
          padding: 24,
          width: "min(560px, 96vw)",
        }}
      >
        <div
          style={{
            fontSize: 10,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            opacity: 0.55,
            marginBottom: 4,
          }}
        >
          Add evidence to
        </div>
        <div
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: 20,
            fontWeight: 300,
            marginBottom: 4,
          }}
        >
          {vessel.name}
        </div>
        <div
          style={{
            fontFamily: "'Inter', sans-serif",
            fontStyle: "italic",
            fontSize: 12,
            opacity: 0.6,
            marginBottom: 18,
            lineHeight: 1.5,
          }}
        >
          Evidence is not understanding. SONMI will observe this artifact
          and propose what she sees. Nothing enters shared understanding
          until it has been reviewed.
        </div>

        <label
          style={{
            fontSize: 10,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            opacity: 0.55,
          }}
        >
          Session
        </label>
        <select
          data-testid="add-evidence-session"
          value={sessionId}
          onChange={(e) => setSessionId(e.target.value)}
          style={selectStyle}
        >
          <option value="">— choose a session —</option>
          {sessions.map((s) => (
            <option key={s.id} value={s.id}>
              {s.title}
            </option>
          ))}
        </select>

        <label
          style={{
            fontSize: 10,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            opacity: 0.55,
            marginTop: 14,
            display: "block",
          }}
        >
          Artifact
        </label>
        <select
          data-testid="add-evidence-artifact"
          value={artifactId}
          onChange={(e) => setArtifactId(e.target.value)}
          disabled={!sessionId}
          style={selectStyle}
        >
          <option value="">— choose an artifact —</option>
          {artifacts.map((a) => (
            <option key={a.id} value={a.id}>
              [{a.type}] {a.title}
            </option>
          ))}
        </select>

        {error && (
          <div
            style={{
              marginTop: 12,
              padding: "8px 10px",
              border: "1px solid rgba(255,120,120,0.4)",
              background: "rgba(255,120,120,0.06)",
              color: "rgba(255,200,200,0.9)",
              fontSize: 11,
              borderRadius: 4,
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            marginTop: 18,
            display: "flex",
            gap: 8,
            justifyContent: "flex-end",
          }}
        >
          <button onClick={onClose} style={btnSmall}>
            cancel
          </button>
          <button
            data-testid="add-evidence-submit"
            onClick={submit}
            disabled={!sessionId || !artifactId || submitting}
            style={{
              ...btnSmall,
              borderColor: "rgba(180,240,200,0.4)",
              color: "rgba(200,240,215,0.95)",
              opacity: !sessionId || !artifactId || submitting ? 0.5 : 1,
            }}
          >
            {submitting ? "adding…" : "Add evidence"}
          </button>
        </div>
      </div>
    </div>
  );
}

const selectStyle = {
  width: "100%",
  marginTop: 6,
  background: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.14)",
  color: "inherit",
  padding: "8px 10px",
  fontSize: 12,
  fontFamily: "inherit",
  borderRadius: 3,
  outline: "none",
  boxSizing: "border-box",
};

/* ---------- Extraction discipline panel ----------
 * Shows the exact system message SONMI was given during extraction, plus
 * the honest note about what she does NOT see (existing concepts). The
 * discipline itself is code-level; this panel is read-only.
 */
function ExtractionDisciplinePanel() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || data) return;
    let cancelled = false;
    api.get("/worlds/extraction_discipline").then((r) => {
      if (!cancelled) setData(r.data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [open, data]);

  return (
    <div style={{ marginBottom: 10 }}>
      <button
        data-testid="extraction-discipline-toggle"
        onClick={() => setOpen((v) => !v)}
        style={{
          background: "transparent",
          border: "1px dashed rgba(255,220,140,0.35)",
          color: "rgba(255,220,140,0.85)",
          fontFamily: "inherit",
          fontSize: 10,
          letterSpacing: "0.10em",
          textTransform: "uppercase",
          padding: "5px 10px",
          borderRadius: 3,
          cursor: "pointer",
        }}
      >
        {open ? "− hide" : "+ show"} what SONMI was told to do
      </button>
      {open && (
        <div
          data-testid="extraction-discipline-panel"
          style={{
            marginTop: 8,
            padding: "12px 14px",
            background: "rgba(0,0,0,0.35)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 4,
            fontFamily: "'Inter', sans-serif",
            fontSize: 11,
            lineHeight: 1.6,
            opacity: 0.88,
            color: "rgba(230,230,240,0.9)",
          }}
        >
          {!data && <div style={{ opacity: 0.5 }}>loading…</div>}
          {data && (
            <>
              <div style={{
                fontSize: 9,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                opacity: 0.55,
                marginBottom: 6,
              }}>
                extraction discipline · model {data.model} · artifact ≤ {data.artifact_content_char_limit.toLocaleString()} chars
              </div>
              <pre style={{
                whiteSpace: "pre-wrap",
                margin: 0,
                fontFamily: "inherit",
                fontSize: 11,
                lineHeight: 1.55,
              }}>{data.system_message}</pre>
              {data.note && (
                <div style={{
                  marginTop: 10,
                  padding: "8px 10px",
                  background: "rgba(255,180,140,0.06)",
                  borderLeft: "3px solid rgba(255,180,140,0.5)",
                  fontSize: 11,
                  lineHeight: 1.55,
                  color: "rgba(255,215,180,0.92)",
                  fontStyle: "italic",
                }}>
                  <span style={{
                    fontSize: 9,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    fontStyle: "normal",
                    opacity: 0.75,
                    marginRight: 8,
                  }}>honest limit</span>
                  {data.note}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
